import React, { useState } from 'react';
import { instagramVisning, instagramLank } from '../../utils/instagram';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * BuilderSection - Visar information om byggföretaget och ansvarig kontaktperson
 * Återanvändbar i både OffertPage och OffertView
 */
const BuilderSection = ({
  offert,
  isExpanded,
  onToggle
}) => {
  const [laddar, setLaddar] = useState(false);
  const [fel, setFel] = useState('');

  const antalIntyg = offert?.builderId?.antalIntyg || 0;

  /**
   * Hämtar den sammanslagna PDF:en.
   *
   * Går via fetch och inte en vanlig länk, eftersom endpointen kräver
   * inloggning och token ligger i localStorage — en <a href> skickar ingen
   * Authorization-header och hade gett 401.
   */
  const laddaNerBilaga = async () => {
    setLaddar(true);
    setFel('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/builders/${offert.builderId._id}/certifications-appendix`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Kunde inte hämta behörighetsbilagan');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${offert.builderId.companyName || 'Byggföretag'} - Behörighetsbilaga.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setFel('Bilagan kunde inte hämtas. Försök igen, eller hör av dig till din byggkonsult.');
    } finally {
      setLaddar(false);
    }
  };

  if (!offert?.builderId) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <button
        onClick={onToggle}
        className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <h2 className="text-lg font-semibold text-leadme-gray-dark">Byggföretag</h2>
        <svg
          className={`w-5 h-5 text-gray-500 transform transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isExpanded && (
        <div className="px-6 pb-6">
          <div>
            {/* Företagsinformation (byggarens kontaktperson visas inte i offerten —
                den syns först när kunden konverterats till projekt) */}
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              {/* Logotyp */}
              <div className="flex-shrink-0 p-4 bg-gray-50 rounded-xl flex items-center justify-center">
                {offert.builderId?.avatar ? (
                  <img
                    src={offert.builderId.avatar}
                    alt={offert.builderId.companyName}
                    className="w-24 h-24 sm:w-36 sm:h-36 rounded-lg object-cover border border-gray-200 shadow-sm"
                  />
                ) : (
                  <div className="w-24 h-24 sm:w-36 sm:h-36 rounded-lg bg-gray-200 border border-gray-300 shadow-sm flex items-center justify-center">
                    <span className="text-3xl sm:text-4xl font-bold text-gray-500">
                      {offert.builderId?.companyName?.charAt(0) || '?'}
                    </span>
                  </div>
                )}
              </div>

              {/* Företagsdetaljer i två kolumner: identitet till vänster, länkar
                  till höger. Som fyra staplade rader blev kortet onödigt högt,
                  och Instagram-tillägget gjorde det värre. Faller tillbaka till
                  en kolumn på mobil. */}
              <div className="grid grid-cols-1 sm:grid-cols-[auto_auto] gap-x-10 gap-y-2 text-center sm:text-left">
                <div className="space-y-2">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Företagsnamn</label>
                    <p className="text-gray-900 font-semibold">
                      {offert.builderId?.companyName || offert.builderId?.name || 'Ej angivet'}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-600">Org nr</label>
                    <p className="text-gray-900 font-medium">
                      {offert.builderId?.organizationNumber || 'Ej angivet'}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Hemsida</label>
                    <p className="text-gray-900 font-medium break-all">
                      {offert.builderId?.website ? (
                        <a
                          href={offert.builderId.website.startsWith('http') ? offert.builderId.website : `https://${offert.builderId.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {offert.builderId.website}
                        </a>
                      ) : 'Ej angivet'}
                    </p>
                  </div>

                  {/* Instagram visas bara när byggaren har ett konto. Att skriva
                      "Ej angivet" för alla andra hade fyllt kortet med brus. */}
                  {instagramVisning(offert.builderId?.instagram) && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Instagram</label>
                      <p className="text-gray-900 font-medium break-all">
                        <a
                          href={instagramLank(offert.builderId.instagram)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {instagramVisning(offert.builderId.instagram)}
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Behörigheter och intyg.
                Kunden vill ofta se dem innan hen accepterar, och frågade då
                byggkonsulten. Visas bara när det finns något att hämta. */}
            {antalIntyg > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <label className="text-sm font-medium text-gray-600">Behörigheter och intyg</label>
                <p className="text-sm text-gray-500 mt-1 mb-3">
                  {antalIntyg === 1
                    ? 'Ett intyg finns samlat i en PDF.'
                    : `${antalIntyg} intyg finns samlade i en PDF.`}
                </p>
                <button
                  type="button"
                  onClick={laddaNerBilaga}
                  disabled={laddar}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {laddar ? 'Hämtar…' : 'Ladda ner behörighetsbilaga'}
                </button>
                {fel && <p className="text-sm text-red-600 mt-2">{fel}</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BuilderSection;

